import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Copy, CheckCircle, ArrowLeft, Users, Calendar, DollarSign, Target, Edit3, ChevronDown } from 'lucide-react';
import { formatDate } from '../../utils/helpers';

// Responsibility labels (unchanged)
const RESPONSIBILITY_LABELS = {
  ground_setup: '🔧 Ground Setup',
  refreshments: '☕ Drinks & Snacks',
  scoring: '📋 Scoring',
  umpiring: '🧑‍⚖️ Umpiring',
  first_aid: '🩹 First Aid',
  equipment: '🎒 Equipment',
  transport: '🚗 Transport',
  photography: '📸 Photography'
};

/**
 * WhatsAppGroupMessenger component
 * - Allows an admin to select a tournament and a game (optional) for messaging.
 * - Uses a club‑wide WhatsApp group link (passed in via `clubWhatsAppLink`).
 * - No longer shows team selection UI.
 */
const WhatsAppGroupMessenger = ({
  players,
  games,
  selectedPlayers,
  onBack,
  userRole,
  userId,
  // optional callbacks for future extensions
  onUpdateGroupLink,
  tournaments = [],
  clubWhatsAppLink = '',
  expenses = [],
  walletTransactions = [],
  teams = []
}) => {
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [selectedTournamentId, setSelectedTournamentId] = useState('');

  // Derived data
  const currentTournament = tournaments.find(t => t.id === selectedTournamentId) || null;
  const tournamentGames = React.useMemo(() => {
    if (!currentTournament) return [];
    return games.filter(g => g.tournamentId === currentTournament.id);
  }, [games, currentTournament]);
  const game = tournamentGames.find(g => g.id === selectedGameId) || null;

  // Compute outstanding player balances for the current tournament
  const playerBalances = React.useMemo(() => {
    if (!currentTournament) return [];

    const tournamentExpenses = expenses.filter(e => {
      if (e.tournamentId === currentTournament.id) return true;
      const gameIds = tournamentGames.map(g => g.id);
      return e.gameId && gameIds.includes(e.gameId);
    });

    const allPlayerIds = new Set();
    const playerGameCount = {};
    const playerExpensesMap = {};

    // Get all players who participated in any tournament game
    tournamentGames.forEach(game => {
      (game.playing11 || []).forEach(playerId => {
        allPlayerIds.add(playerId);
        playerGameCount[playerId] = (playerGameCount[playerId] || 0) + 1;
      });
    });

    // Calculate expenses per player
    tournamentExpenses.forEach(expense => {
      const game = expense.gameId ? tournamentGames.find(g => g.id === expense.gameId) : null;
      
      if (game && game.playing11 && game.playing11.length > 0) {
        // PER-GAME EXPENSE
        const squadSize = game.playing11.length;
        const superSubCount = game.superSubs?.length || 0;
        const regularCount = squadSize - superSubCount;
        const totalShares = regularCount + (superSubCount * 0.5);

        game.playing11.forEach(playerId => {
          const isSuperSub = game.superSubs?.includes(playerId);
          const share = isSuperSub
            ? (expense.amount / totalShares) * 0.5
            : expense.amount / totalShares;
          playerExpensesMap[playerId] = (playerExpensesMap[playerId] || 0) + share;
        });
      } else if (!game || expense.level === 'tournament') {
        // TOURNAMENT-WIDE EXPENSE
        const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
        const numGames = validGames.length;
        
        if (numGames > 0) {
          const expensePerGame = expense.amount / numGames;
          
          validGames.forEach(g => {
            const squadSize = g.playing11.length;
            const superSubCount = g.superSubs?.length || 0;
            const regularCount = squadSize - superSubCount;
            const totalShares = regularCount + (superSubCount * 0.5);

            g.playing11.forEach(playerId => {
              const isSuperSub = g.superSubs?.includes(playerId);
              const share = isSuperSub
                ? (expensePerGame / totalShares) * 0.5
                : expensePerGame / totalShares;
              playerExpensesMap[playerId] = (playerExpensesMap[playerId] || 0) + share;
            });
          });
        }
      }
    });

    // Calculate late fees per player
    tournamentGames.forEach(game => {
      if (game.lateArrivals) {
        Object.keys(game.lateArrivals).forEach(playerId => {
          if (game.lateArrivals[playerId]) {
            const lateFee = game.lateFeeAmount ?? 5;
            playerExpensesMap[playerId] = (playerExpensesMap[playerId] || 0) + lateFee;
          }
        });
      }
    });

    // Helpers to resolve player info
    const getPlayerInfo = (playerId) => {
      const p = players.find(x => x.id === playerId || x.userId === playerId);
      if (p) return p;
      for (const team of teams) {
        const tp = team.players?.find(x => x.id === playerId || x.userId === playerId);
        if (tp) return tp;
      }
      return { name: 'Unknown Player', email: '' };
    };

    return Array.from(allPlayerIds).map(playerId => {
      const info = getPlayerInfo(playerId);
      const totalOwed = playerExpensesMap[playerId] || 0;

      // Calculate paid amount
      const paid = walletTransactions
        .filter(t =>
          (t.userId === playerId || (info.email && t.playerEmail?.toLowerCase() === info.email.toLowerCase())) &&
          (t.type === 'payment' || t.type === 'waiver') &&
          t.status === 'approved' &&
          t.amount < 0 &&
          !t.isDonation &&
          (t.tournamentId === currentTournament.id || tournamentGames.some(g => g.id === t.gameId))
        )
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

      const balance = totalOwed - paid;
      const hasPending = walletTransactions.some(t =>
        (t.userId === playerId || (info.email && t.playerEmail?.toLowerCase() === info.email.toLowerCase())) &&
        t.type === 'payment' &&
        t.status === 'pending' &&
        (t.tournamentId === currentTournament.id || tournamentGames.some(g => g.id === t.gameId))
      );
      return {
        id: playerId,
        name: info.name,
        balance,
        gamesPlayed: playerGameCount[playerId] || 0,
        hasPending
      };
    })
    .filter(p => p.balance > 0.005) // Only players with outstanding balance
    .sort((a, b) => b.balance - a.balance);
  }, [currentTournament, games, expenses, walletTransactions, players, teams]);

  // Message templates – only those that make sense for a tournament context
  const templates = {
    gameReminder: {
      name: `${String.fromCodePoint(0x1F3CF)} Game Reminder`,
      icon: Calendar,
      generate: () => {
        if (!game) return `Please select a game first`;
        const opponent = game.opponent || 'TBD';
        const dateStr = formatDate(game.date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        return `${String.fromCodePoint(0x1F3CF)} *${currentTournament?.name || 'Tournament'} - Game Reminder*\n\n`
          + `${String.fromCodePoint(0x1F4C5)} *Date:* ${dateStr}\n`
          + `${String.fromCodePoint(0x23F0)} *Time:* ${game.time || 'TBD'}\n`
          + `${String.fromCodePoint(0x1F4CD)} *Venue:* ${game.venue || 'TBD'}\n`
          + `${String.fromCodePoint(0x1F19A)} *Opponent:* ${opponent}\n\n`
          + `${String.fromCodePoint(0x26A0, 0xFE0F)} *Please confirm your availability ASAP!*`;
      }
    },
    paymentReminder: {
      name: `${String.fromCodePoint(0x1F4B0)} Payment Reminder`,
      icon: DollarSign,
      generate: () => {
        const tournamentName = currentTournament?.name || 'Tournament';
        const displayTournamentName = tournamentName.toLowerCase().includes('tournament')
          ? tournamentName
          : `${tournamentName} Tournament`;

        let msg = `Please check the list below for the outstanding tournament fees regarding the ${displayTournamentName}.\n`
          + `Note: Once you have paid, Please don’t forget to record the payment in the Clubhudl.\n\n`
          + `👤 Player Name:💰 Outstanding Balance🏏 (Games Played)\n`
          + `──────────────────────\n`;

        if (playerBalances && playerBalances.length > 0) {
          playerBalances.forEach(p => {
            msg += `•  ${p.name}: $${p.balance.toFixed(2)} (${p.gamesPlayed} game${p.gamesPlayed === 1 ? '' : 's'} played)${p.hasPending ? '✅' : ''}\n`;
          });
          msg += `\n`;
        }

        msg += `Thanks for your cooperation! 🙏`;
        return msg;
      }
    },
    custom: {
      name: `${String.fromCodePoint(0x270D, 0xFE0F)} Custom Message`,
      icon: Edit3,
      generate: () => ''
    }
  };

  // Auto‑generate message when template changes
  useEffect(() => {
    if (selectedTemplate && selectedTemplate !== 'custom') {
      const tmpl = templates[selectedTemplate];
      if (tmpl) {
        setMessage(tmpl.generate());
        setCustomMode(false);
      }
    } else if (selectedTemplate === 'custom') {
      setCustomMode(true);
      setMessage('');
    }
  }, [selectedTemplate, game, currentTournament, playerBalances]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendToWhatsApp = () => {
    if (!message) return;
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      if (clubWhatsAppLink) {
        window.open(clubWhatsAppLink, '_blank');
      } else {
        window.open('https://web.whatsapp.com/', '_blank');
      }
    }).catch(err => {
      console.error('Failed to copy message:', err);
      alert('Failed to copy message. Please copy manually.');
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors">
          <ArrowLeft size={20} className="mr-2" /> Back to Overview
        </button>
        <h2 className="text-3xl font-bold text-gray-900">WhatsApp Group Messenger</h2>
        <p className="text-gray-600 mt-1">Send notifications to your tournament's WhatsApp group</p>
      </div>

      {/* Tournament Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Tournament</label>
        <select
          value={selectedTournamentId}
          onChange={(e) => { setSelectedTournamentId(e.target.value); setSelectedGameId(''); }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="">Choose a tournament…</option>
          {tournaments.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Game Selection (optional) */}
      {currentTournament && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Game (optional)</label>
          <select
            value={selectedGameId}
            onChange={(e) => setSelectedGameId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All tournament games</option>
            {tournamentGames.map(g => (
              <option key={g.id} value={g.id}>{g.name} – {formatDate(g.date)}</option>
            ))}
          </select>
        </div>
      )}

      {/* Template Selection */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center"><MessageCircle size={20} className="mr-2 text-blue-600" />Message Templates</h3>
          <div className="space-y-2">
            {Object.entries(templates).map(([key, tmpl]) => (
              <button
                key={key}
                onClick={() => setSelectedTemplate(key)}
                className={`w-full flex items-center p-3 rounded-lg transition-all ${selectedTemplate === key ? 'bg-green-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
              >
                <tmpl.icon size={18} className="mr-3" />
                <span className="text-sm font-medium">{tmpl.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Message Editor & Preview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message Editor */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Message Editor</h3>
              {selectedTemplate && !customMode && (
                <button onClick={() => setCustomMode(true)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center">
                  <Edit3 size={14} className="mr-1" />Edit Message
                </button>
              )}
            </div>
            {!selectedTemplate ? (
              <div className="text-center py-12 text-gray-500">
                <MessageCircle size={48} className="mx-auto mb-3 opacity-50" />
                <p>Select a template to get started</p>
              </div>
            ) : (
              <textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setCustomMode(true); }}
                placeholder="Type your message here..."
                className="w-full h-80 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none font-mono text-sm"
                disabled={!customMode && selectedTemplate !== 'custom'}
              />
            )}
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">{message.length} characters</p>
              <button onClick={() => copyToClipboard(message)} className="flex items-center text-sm text-gray-600 hover:text-gray-800">
                {copied ? <><CheckCircle size={16} className="mr-1 text-green-600" />Copied!</> : <><Copy size={16} className="mr-1" />Copy Message</>}
              </button>
            </div>
          </div>

          {/* Preview */}
          {message && (
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center"><MessageCircle size={20} className="mr-2 text-green-600" />Message Preview</h3>
              <div className="bg-white rounded-2xl rounded-tl-none shadow-lg p-6 max-w-md relative">
                <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-white border-r-[12px] border-r-transparent"></div>
                <div className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">{message}</div>
                <div className="text-xs text-gray-400 mt-3 text-right">{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>
          )}

          {/* Action Button */}
          {message && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <button onClick={sendToWhatsApp} className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-4 rounded-xl font-semibold hover:from-green-700 hover:to-green-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center group">
                <MessageCircle size={24} className="mr-3 group-hover:scale-110 transition-transform" />
                {copied ? <><CheckCircle size={20} className="mr-2" />Copied! Opening WhatsApp...</> : <><Copy size={20} className="mr-2" />Copy & Open WhatsApp</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppGroupMessenger;